import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import { Button, Modal, TextArea, useToast } from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import { buildId } from '@/lib/client-errors';
import { FormError } from '@/components/FormError';

/**
 * «أبلغ عن مشكلة» (29 سبتمبر 2026): من داخل المنصة، بضغطةٍ من قائمة «؟». يصل صندوقَ التواصل نفسَه
 * (`POST /me/problem-reports`) باسم صاحب الحساب وبريدِه من صفّه — لا يُسأل عمّا نعرفه — وتُلحَق به
 * الشاشةُ التي كان عليها وصفتُه ورقمُ البناء تلقائياً، فيعرف من يعالج أين وقع ما وقع.
 */
export function ReportProblemDialog({ onClose }: { onClose: () => void }) {
  const { pathname, search } = useLocation();
  const { roles } = useAuth();
  const toast = useToast();
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const text = message.trim();
    if (text.length < 10) { setError('اكتب ما حدث في عشرة أحرفٍ على الأقل.'); setAttempt((a) => a + 1); return; }
    setBusy(true);
    try {
      await api.post('/me/problem-reports', { message: text, screen: `${pathname}${search}`.slice(0, 300), role: roles[0], build: buildId() }, { institutionId: null });
      toast.push('success', 'وصل بلاغك — شكراً لك. نتابعه ونعود إليك على بريدك إن لزم.');
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'تعذّر إرسال البلاغ الآن — حاول بعد قليل.');
      setAttempt((a) => a + 1);
    } finally { setBusy(false); }
  };

  return (
    <Modal
      title="أبلغ عن مشكلة"
      description={`نُلحق بالبلاغ اسمَ الشاشة الحالية (${pathname}) تلقائياً — فلا حاجة لكتابته.`}
      onClose={onClose}
      footer={<><Button variant="secondary" onClick={onClose}>إلغاء</Button><Button variant="primary" onClick={() => void submit()} loading={busy} data-testid="report-send">أرسل البلاغ</Button></>}
    >
      <div className="stack gap-3">
        <TextArea
          label="ماذا حدث؟"
          hint="ما الذي ضغطته، وما الذي توقّعته، وما الذي رأيته. لا تكتب كلمة مرورٍ ولا رقمَ هويةٍ لأحد."
          rows={5} value={message} onChange={(e) => setMessage(e.target.value)} autoFocus
          data-testid="report-text"
        />
        {error && <FormError key={attempt}>{error}</FormError>}
      </div>
    </Modal>
  );
}
